import { useEffect, useState } from 'react'

import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import FormGroup from '@/core/components/FormGroup'
import {
  RewardPoolDto,
  RewardRuleDto,
  RewardRuleUpsertDto,
  TRIGGER_TYPES,
  TriggerType,
} from '@/common/models/gamification'

const NO_POOL = 'none'

interface RewardRuleEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rule?: RewardRuleDto | null
  pools: RewardPoolDto[]
  onSubmit: (data: RewardRuleUpsertDto, id?: string) => Promise<void> | void
}

export const RewardRuleEditorModal = ({
  open,
  onOpenChange,
  rule,
  pools,
  onSubmit,
}: RewardRuleEditorModalProps) => {
  const isEditMode = !!rule

  const [triggerType, setTriggerType] = useState<TriggerType>('CHALLENGE')
  const [triggerRef, setTriggerRef] = useState('')
  const [rewardPoolId, setRewardPoolId] = useState(NO_POOL)
  const [active, setActive] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    if (rule) {
      setTriggerType(rule.triggerType)
      setTriggerRef(rule.triggerRef ?? '')
      setRewardPoolId(rule.rewardPoolId ?? NO_POOL)
      setActive(rule.active)
    } else {
      setTriggerType('CHALLENGE')
      setTriggerRef('')
      setRewardPoolId(NO_POOL)
      setActive(true)
    }
  }, [open, rule])

  const triggerRefRequired = triggerType === 'CHALLENGE'
  const triggerRefInvalid = triggerRefRequired && !triggerRef.trim()
  const invalid = triggerRefInvalid

  const handleClose = () => onOpenChange(false)

  const submit = async () => {
    if (invalid) return
    const data: RewardRuleUpsertDto = {
      triggerType,
      triggerRef: triggerRef.trim() || undefined,
      rewardPoolId: rewardPoolId === NO_POOL ? undefined : rewardPoolId,
      active,
    }
    try {
      setSaving(true)
      await onSubmit(data, rule?.id)
      handleClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose()
      }}
      size="md"
      title={
        <div className="border-b border-monochrome-120 pb-4">
          <PageTitle
            icon="gift"
            title={
              <span className="flex items-center gap-2">
                {isEditMode ? 'Edit' : 'Add New'}{' '}
                <span className="text-accent-1">Rule</span>
              </span>
            }
          />
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button onClick={handleClose} type="button" variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={submit}
            type="button"
            variant="primary"
            disabled={invalid || saving}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <SelectField
          label="Trigger Type"
          required
          value={triggerType}
          onValueChange={(v) => setTriggerType(v as TriggerType)}
          options={TRIGGER_TYPES.map((t) => ({ value: t, label: t }))}
        />

        <FormGroup
          label={`Trigger Ref${triggerRefRequired ? ' *' : ''}`}
          error={
            triggerRefInvalid
              ? 'Required (UUID) when trigger is CHALLENGE'
              : undefined
          }
        >
          <Input
            value={triggerRef}
            isInvalid={triggerRefInvalid}
            placeholder="e.g. a challenge UUID"
            onChange={(e) => setTriggerRef(e.target.value)}
          />
        </FormGroup>

        <SelectField
          label="Reward Pool"
          placeholder="None"
          value={rewardPoolId}
          onValueChange={setRewardPoolId}
          options={[
            { value: NO_POOL, label: 'None' },
            ...pools.map((pool) => ({ value: pool.id, label: pool.id })),
          ]}
        />

        <label className="flex items-center gap-3 cursor-pointer">
          <Checkbox
            checked={active}
            onCheckedChange={(checked) => setActive(!!checked)}
          />
          <span className="text-body-medium text-monochrome-1">Active</span>
        </label>
      </div>
    </Modal>
  )
}
